import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { BackButton } from "../components/BackButton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Plus, Package, ArrowRight, Search, X, MoreVertical, Pencil, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { VEHICLE_LABEL } from "../lib/pricing";
import BillingInfoCard from "../components/BillingInfoCard";
import { TiltCard3D, StaggerReveal } from "../components/motion/MotionPremium";
import { CountUp } from "../components/motion/MotionPrimitives";

const STATUS_LABEL = {
  open: "OUVERTE",
  accepted: "ACCEPTÉE",
  loaded: "CHARGÉE",
  en_route: "EN ROUTE",
  delivered: "LIVRÉE",
  cancelled: "ANNULÉE",
};

const PAYMENT_LABEL = {
  unpaid: { label: "À PAYER", color: "bg-amber-500 text-white" },
  pending: { label: "EN COURS", color: "bg-amber-400 text-white" },
  paid: { label: "EN SÉQUESTRE", color: "bg-blue-600 text-white" },
  payout_pending: { label: "VERSEMENT EN COURS", color: "bg-amber-500 text-white" },
  released: { label: "REVERSÉE", color: "bg-emerald-600 text-white" },
  refund_pending: { label: "REMBOURSEMENT EN COURS", color: "bg-amber-500 text-white" },
  partial_refunded: { label: "REMBOURSEMENT PARTIEL", color: "bg-orange-500 text-white" },
  partially_refunded: { label: "REMBOURSEMENT PARTIEL", color: "bg-orange-500 text-white" },
  refunded: { label: "REMBOURSÉE", color: "bg-red-600 text-white" },
};

// A mission is editable/deletable only when open AND not paid
function canEditOrDelete(m) {
  return m.status === "open" && (!m.payment_status || m.payment_status === "unpaid");
}

export default function ShipperDashboard() {
  const [missions, setMissions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // mission object being edited
  const [deleting, setDeleting] = useState(null); // mission object being deleted

  const load = async () => {
    try {
      const [m, u] = await Promise.all([
        api.get("/missions?view=shipper"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setMissions(m.data || []);
      if (u.data) setUser(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return missions;
    return missions.filter((m) =>
      (m.title || "").toLowerCase().includes(q) ||
      (m.pickup_address || "").toLowerCase().includes(q) ||
      (m.delivery_address || "").toLowerCase().includes(q) ||
      (m.id || "").toLowerCase().startsWith(q)
    );
  }, [missions, search]);

  const stats = {
    total: missions.length,
    open: missions.filter(m => m.status === "open").length,
    active: missions.filter(m => ["accepted", "en_route", "loaded"].includes(m.status)).length,
    delivered: missions.filter(m => m.status === "delivered").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="shipper-dashboard">
      <BackButton to="/" label="Retour à l'accueil" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline text-accent mb-2">— Expéditeur</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Mes missions</h1>
        </div>
        <Link to="/missions/new" data-testid="btn-new-mission">
          <Button className="rounded-md bg-[#1F4032] hover:bg-[#2A5443] h-11 gap-2">
            <Plus className="w-4 h-4" /> Publier une mission
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          ["Total", stats.total],
          ["Ouvertes", stats.open],
          ["Actives", stats.active],
          ["Livrées", stats.delivered],
        ].map(([l, v]) => (
          <TiltCard3D key={l} intensity={9} className="bg-white p-5 border border-[#1F4032]/10 rounded-md shadow-md shadow-[#1F4032]/5">
            <div className="overline text-muted-foreground">{l}</div>
            <div className="font-display font-bold text-3xl mt-2 bg-gradient-to-r from-[#1F4032] to-[#5A917A] bg-clip-text text-transparent">
              <CountUp value={v} duration={750} />
            </div>
          </TiltCard3D>
        ))}
      </div>

      <BillingInfoCard user={user} onSaved={setUser} />

      <div className="mb-10" />

      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">CHARGEMENT…</div>
      ) : missions.length === 0 ? (
        <div className="border border-dashed border-[#1F4032]/20 p-12 text-center">
          <Package className="w-8 h-8 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-muted-foreground mb-6">Aucune mission pour le moment.</p>
          <Link to="/missions/new">
            <Button className="rounded-md bg-[#1F4032] hover:bg-[#2A5443]">Publier ma première mission</Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Barre de recherche */}
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, adresse ou ID…"
              className="pl-9 pr-9 h-10 rounded-md"
              data-testid="shipper-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F4032]"
                aria-label="Effacer"
                data-testid="shipper-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {filtered.length === 0 ? (
            <div className="border border-dashed border-[#1F4032]/20 p-10 text-center text-muted-foreground">
              Aucun résultat pour <b>"{search}"</b>.{" "}
              <button onClick={() => setSearch("")} className="underline">Effacer</button>
            </div>
          ) : (
            <div className="border border-[#1F4032]/10">
              {filtered.map((m) => {
                const editable = canEditOrDelete(m);
                return (
                  <div key={m.id}
                    className="relative flex flex-wrap items-center justify-between gap-4 p-5 border-b border-[#1F4032]/10 last:border-0 hover:bg-muted/50 transition-colors"
                    data-testid={`mission-row-${m.id}`}>
                    <Link to={`/missions/${m.id}`} className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-muted-foreground mb-1">
                        {m.pickup_address} → {m.delivery_address}
                      </div>
                      <div className="font-display font-bold text-lg truncate">{m.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {m.distance_km} km · {m.cargo_weight_kg} kg · {(VEHICLE_LABEL[m.vehicle_type] || m.vehicle_type || "").toUpperCase()}
                      </div>
                    </Link>
                    <div className="flex items-center gap-3 md:gap-6 flex-wrap">
                      <div className="font-display font-bold text-xl">{m.price}€</div>
                      {m.payment_status && PAYMENT_LABEL[m.payment_status] && (
                        <span className={`px-2 py-1 text-[10px] font-mono rounded-md ${PAYMENT_LABEL[m.payment_status].color}`}>
                          💳 {PAYMENT_LABEL[m.payment_status].label}
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-mono rounded-md ${m.status === "delivered" ? "bg-[#1F4032] text-white" : m.status === "open" ? "bg-[#1F4032] text-white" : "bg-muted text-black"}`}>
                        {STATUS_LABEL[m.status] || m.status}
                      </span>
                      {editable ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="p-2 rounded-md hover:bg-[#1F4032]/10 text-[#1F4032]"
                              aria-label="Actions"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`mission-actions-${m.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); setEditing(m); }}
                              data-testid={`mission-edit-${m.id}`}
                              className="cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); setDeleting(m); }}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                              data-testid={`mission-delete-${m.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : m.status === "delivered" ? (
                        <Link
                          to={`/missions/new?duplicate=${m.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono rounded-md border border-[#1F4032] text-[#1F4032] hover:bg-[#1F4032] hover:text-white transition-colors"
                          title="Recréer une mission identique"
                          data-testid={`mission-duplicate-${m.id}`}
                        >
                          <Copy className="w-3.5 h-3.5" /> RE-PUBLIER
                        </Link>
                      ) : (
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <EditMissionDialog
        mission={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <DeleteMissionDialog
        mission={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => { setDeleting(null); load(); }}
      />
    </div>
  );
}

function EditMissionDialog({ mission, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", pickup_date: "", contact_phone: "", price: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mission) {
      // pickup_date can be "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM" — datetime-local needs YYYY-MM-DDTHH:MM
      const raw = mission.pickup_date || "";
      const dt = raw.length === 10 ? `${raw}T09:00` : raw.slice(0, 16);
      setForm({
        title: mission.title || "",
        description: mission.description || "",
        pickup_date: dt,
        contact_phone: mission.contact_phone || "",
        price: mission.price ?? "",
      });
    }
  }, [mission]);

  if (!mission) return null;

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre obligatoire"); return; }
    if (!form.pickup_date) { toast.error("Date d'enlèvement obligatoire"); return; }
    if (!form.contact_phone.trim()) { toast.error("Téléphone obligatoire"); return; }
    const price = Number(form.price);
    if (!Number.isFinite(price) || price <= 0) { toast.error("Prix obligatoire et supérieur à 0 €"); return; }
    setSaving(true);
    try {
      await api.patch(`/missions/${mission.id}`, {
        title: form.title.trim(),
        description: form.description.trim(),
        pickup_date: form.pickup_date,
        contact_phone: form.contact_phone.trim(),
        price,
      });
      toast.success("Mission modifiée");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!mission} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-mission-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">Modifier la mission</DialogTitle>
          <DialogDescription className="text-xs">
            Le prix reste libre et sa ventilation 15 % MML / 85 % transporteur est recalculée côté serveur.
            Le titre, la description, la date et le téléphone sont également modifiables.
            Pour changer l'adresse, le poids ou le véhicule, supprimez cette mission et créez-en une nouvelle.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline">Prix proposé (€)</Label>
            <Input
              type="number"
              min="1"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="mt-1 h-11 rounded-md"
              data-testid="edit-price-input"
            />
            <p className="text-xs text-neutral-500 mt-1">Un prix trop bas peut réduire le nombre de candidatures.</p>
          </div>
          <div>
            <Label className="overline">Titre</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 h-11 rounded-md"
              data-testid="edit-title-input"
            />
          </div>
          <div>
            <Label className="overline">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1 rounded-md"
              data-testid="edit-description-input"
            />
          </div>
          <div>
            <Label className="overline">Date d'enlèvement</Label>
            <Input
              type="datetime-local"
              value={form.pickup_date}
              onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
              className="mt-1 h-11 rounded-md"
              data-testid="edit-date-input"
            />
          </div>
          <div>
            <Label className="overline">Téléphone de contact</Label>
            <Input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="mt-1 h-11 rounded-md"
              data-testid="edit-phone-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} className="rounded-full">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2"
            data-testid="edit-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteMissionDialog({ mission, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  if (!mission) return null;

  const confirm = async () => {
    setDeleting(true);
    try {
      await api.delete(`/missions/${mission.id}`);
      toast.success("Mission supprimée");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={!!mission} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="delete-mission-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display font-black tracking-tight">Supprimer cette mission&nbsp;?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block font-medium text-foreground">« {mission.title} »</span>
            <span className="block text-xs mt-2">
              Cette action est définitive. Les messages et notifications associés seront aussi supprimés.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-confirm-btn"
          >
            {deleting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Suppression…</> : "Supprimer définitivement"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
